/* Catalog wipe — list -> detail page transition.
   Ported from the sofas-exclusive-catalog.html prototype, split across a
   real navigation: clicking a product link plays the "cover" half of the
   sweep, then navigates; the next page (flagged via sessionStorage) starts
   covered and plays the "reveal" half. Honors prefers-reduced-motion. */
(function () {
  'use strict';

  const FLAG = 'catalog:wipe';
  const LABEL_KEY = 'catalog:wipe:label';
  const COVER_MS = 480;
  const REVEAL_MS = 480;
  /* If navigation hasn't unloaded the page this long after a click (offline,
     stalled server), lift the cover so the user isn't stuck behind it. */
  const NAV_TIMEOUT_MS = 8000;
  const SCROLL_KEY_PREFIX = 'catalog:scroll:';
  const HISTORY_ENTRY_KEY = 'catalog:history-entry';

  /* Safari can report a reconstructed Back navigation as "navigate" rather
     than "back_forward". Mark each browser-history entry ourselves so a
     return to an existing entry remains distinguishable from a new link. */
  const arrivedOnExistingHistoryEntry = Boolean(
    history.state && history.state[HISTORY_ENTRY_KEY],
  );

  function markCurrentHistoryEntry() {
    try {
      history.replaceState(
        { ...(history.state || {}), [HISTORY_ENTRY_KEY]: true },
        document.title,
      );
    } catch (e) {}
  }

  markCurrentHistoryEntry();

  /* Full-page navigation is intentional for the wipe. On iOS, however,
     memory-heavy WebGL pages can evict the previous document from bfcache;
     Safari then reconstructs it at the top. Persist each URL's position so
     Back restores the reader's place even without a bfcache snapshot. */
  function scrollKey() {
    return SCROLL_KEY_PREFIX + window.location.pathname + window.location.search;
  }

  function saveScrollPosition() {
    try {
      sessionStorage.setItem(
        scrollKey(),
        JSON.stringify({ x: window.scrollX, y: window.scrollY }),
      );
    } catch (e) {}
  }

  function navigationType() {
    const entries = performance.getEntriesByType && performance.getEntriesByType('navigation');
    if (entries && entries[0]) return entries[0].type;
    if (performance.navigation && performance.navigation.type === performance.navigation.TYPE_BACK_FORWARD) {
      return 'back_forward';
    }
    if (performance.navigation && performance.navigation.type === performance.navigation.TYPE_RELOAD) {
      return 'reload';
    }
    return 'navigate';
  }

  function restoreScrollPosition(force = false) {
    /* A saved position belongs only to a browser Back/Forward traversal.
       Applying it to every normal navigation made fresh product and
       collection links reopen halfway down when that URL had been visited. */
    const type = navigationType();
    if (
      !force &&
      (type === 'reload' || (type !== 'back_forward' && !arrivedOnExistingHistoryEntry))
    ) {
      return;
    }

    let position = null;
    try {
      const stored = sessionStorage.getItem(scrollKey());
      if (stored) position = JSON.parse(stored);
      sessionStorage.removeItem(scrollKey());
    } catch (e) {}
    if (!position || !Number.isFinite(position.y) || position.y <= 0) return;

    const restore = () => window.scrollTo(position.x || 0, position.y);
    /* Wait for the document's first layout, then make one post-load pass for
       pages whose section content arrives after DOMContentLoaded. */
    requestAnimationFrame(() => requestAnimationFrame(restore));
    window.addEventListener('load', () => setTimeout(restore, 0), { once: true });
  }

  try {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  } catch (e) {}
  window.addEventListener('pagehide', saveScrollPosition);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveScrollPosition();
  });
  restoreScrollPosition();

  /* Set by the inline pre-paint script in layout/theme.liquid so arrivals are
     covered before first paint; this script takes over from it. */
  function dropPrepaintCover() {
    document.documentElement.classList.remove('catalog-wipe-cover');
  }

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    dropPrepaintCover();
    try {
      sessionStorage.removeItem(FLAG);
    } catch (e) {}
    return;
  }

  const style = document.createElement('style');
  style.textContent =
    '#catalog-wipe{position:fixed;inset:0;z-index:1000;background:var(--catalog-camel);' +
    'transform:translateX(-102%);pointer-events:none;display:flex;align-items:center;justify-content:center;' +
    'padding:0 clamp(2.4rem,8vw,8rem);box-sizing:border-box}' +
    '#catalog-wipe .catalog-wipe__wm{font-weight:600;letter-spacing:.5em;font-size:1.2rem;' +
    'text-transform:uppercase;color:var(--catalog-white);opacity:0;transition:opacity .3s ease .15s;' +
    'max-width:100%;text-align:center;line-height:2;text-indent:.5em}' +
    '#catalog-wipe.is-covering{transition:transform ' +
    COVER_MS +
    'ms cubic-bezier(.22,.8,.24,1);transform:translateX(0)}' +
    '#catalog-wipe.is-covering .catalog-wipe__wm{opacity:1}' +
    '#catalog-wipe.is-covered{transform:translateX(0)}' +
    '#catalog-wipe.is-covered .catalog-wipe__wm{opacity:1}' +
    '#catalog-wipe.is-revealing{transition:transform ' +
    REVEAL_MS +
    'ms cubic-bezier(.22,.8,.24,1);transform:translateX(102%)}' +
    '#catalog-wipe.is-revealing .catalog-wipe__wm{opacity:0}';
  document.head.appendChild(style);

  const overlay = document.createElement('div');
  overlay.id = 'catalog-wipe';
  overlay.setAttribute('aria-hidden', 'true');
  const wm = document.createElement('span');
  wm.className = 'catalog-wipe__wm';

  function mount() {
    const heading = document.querySelector('.header__heading-link, .header__heading');
    /* Image-logo headers have no text, so fall back to the document title
       trimmed at its first separator (en/em dash or pipe) — SEO titles
       like "3D Showroom — Walk the Floor | …" would otherwise fill the
       whole curtain. */
    wm.textContent =
      arrivalLabel ||
      (heading && heading.textContent.trim()) ||
      document.title.split(/[–—|]/)[0].trim();
    overlay.appendChild(wm);
    document.body.appendChild(overlay);
  }

  /* ---------- reveal phase (arriving via wipe) ---------- */
  let arriving = false;
  let arrivalLabel = '';
  try {
    arriving = sessionStorage.getItem(FLAG) === '1';
    if (arriving) sessionStorage.removeItem(FLAG);
    arrivalLabel = sessionStorage.getItem(LABEL_KEY) || '';
    if (arriving) sessionStorage.removeItem(LABEL_KEY);
  } catch (e) {}

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* Sweep an already-covered overlay off screen. */
  function reveal() {
    overlay.classList.remove('is-covered');
    overlay.classList.add('is-revealing');
    setTimeout(() => overlay.classList.remove('is-revealing'), REVEAL_MS + 100);
  }

  /* Mount as early as possible (the script is deferred, so the DOM exists):
     an arriving page must be covered by the real overlay before the inline
     pre-paint cover is dropped, not first at DOMContentLoaded. */
  function boot() {
    mount();
    if (arriving) overlay.classList.add('is-covered');
    dropPrepaintCover();
    if (arriving) {
      onReady(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(reveal);
        });
      });
    }
  }

  if (document.body) {
    boot();
  } else {
    onReady(boot);
  }

  let navigating = false;

  function productLabel(link) {
    const card = link.closest('.product-card-wrapper, .card-wrapper, product-component');
    const heading = card && card.querySelector('.card__heading');
    return (
      (link.dataset.productTitle ||
        (heading && heading.textContent) ||
        link.getAttribute('aria-label') ||
        '').trim()
    );
  }

  /* bfcache restore: never leave the page covered */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      navigating = false;
      /* A page returning from bfcache resumes this script instead of running
         its initial restoration pass, so restore the position saved on the
         preceding pagehide before showing the page again. */
      restoreScrollPosition(true);
      dropPrepaintCover();
      overlay.classList.remove('is-covering', 'is-covered', 'is-revealing');
    }
  });

  /* ---------- cover phase (leaving toward a product page) ----------
     Delegated on document so it survives facets.js AJAX re-renders of
     the collection grid. */
  document.addEventListener('click', (e) => {
    if (navigating || e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const link = e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!link) return;
    if (link.target && link.target !== '_self') return;
    let url;
    try {
      url = new URL(link.href, location.href);
    } catch (err) {
      return;
    }
    if (url.origin !== location.origin) return;
    /* Skip in the theme editor preview to not fight the editor. */
    if (window.Shopify && window.Shopify.designMode) return;

    e.preventDefault();
    navigating = true;
    saveScrollPosition();
    const targetLabel = productLabel(link);
    if (targetLabel) wm.textContent = targetLabel;
    try {
      sessionStorage.setItem(FLAG, '1');
      if (targetLabel) sessionStorage.setItem(LABEL_KEY, targetLabel);
    } catch (err) {}
    overlay.classList.add('is-covering');
    let navigationStarted = false;
    const navigate = () => {
      if (navigationStarted) return;
      navigationStarted = true;
      location.href = url.href;
    };
    const onCoverComplete = (event) => {
      if (event.target !== overlay || event.propertyName !== 'transform') return;
      overlay.removeEventListener('transitionend', onCoverComplete);
      navigate();
    };

    // Navigate as soon as the browser reports that the actual wipe has
    // completed, rather than adding a fixed delay after its 480 ms duration.
    // The timeout only covers browsers that fail to emit transitionend.
    overlay.addEventListener('transitionend', onCoverComplete);
    setTimeout(navigate, COVER_MS + 100);

    /* Failsafe: if the navigation never unloads this page (offline, stalled
       server), sweep the cover back off and allow another attempt. */
    setTimeout(() => {
      if (!navigating) return;
      navigating = false;
      try {
        sessionStorage.removeItem(FLAG);
      } catch (err) {}
      overlay.classList.remove('is-covering');
      overlay.classList.add('is-covered');
      requestAnimationFrame(() => {
        requestAnimationFrame(reveal);
      });
    }, NAV_TIMEOUT_MS);
  });
})();
